import React from "react";
import { Link, useLocation } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, Navigation } from "lucide-react";
import { CONTACT, PREPARE_VISIT } from "../../data/content";
import { Reveal } from "./Reveal";
import { openCookieSettings } from "./CookieConsent";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Statut d'ouverture selon le fuseau Europe/Paris.
// Retourne l'index de la ligne du jour (0 Lundi, 1 Mardi–Vendredi, 2 Samedi–Dimanche)
// et si l'institut est actuellement ouvert.
const getTodayStatus = () => {
  const paris = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Europe/Paris" })
  );
  const day = paris.getDay(); // 0 Dim ... 6 Sam
  const minutes = paris.getHours() * 60 + paris.getMinutes();

  let index;
  let open = false;
  if (day === 1) {
    index = 0; // Lundi 13h15–19h00
    open = minutes >= 13 * 60 + 15 && minutes < 19 * 60;
  } else if (day >= 2 && day <= 5) {
    index = 1; // Mardi–Vendredi 10h15–19h00
    open = minutes >= 10 * 60 + 15 && minutes < 19 * 60;
  } else {
    index = 2; // Samedi–Dimanche : fermé
    open = false;
  }
  return { index, open };
};

export const Contact = () => {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const today = getTodayStatus();
  return (
    <footer
      id="contact"
      data-testid="contact"
      className="bg-white border-t border-[#ece4d7]"
    >
      <Reveal className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <p className="text-xs tracking-[0.25em] uppercase text-[#b07a63] mb-4 text-center">
          Contact
        </p>
        <h2 className="font-serif-display font-light text-[#4b2018] text-4xl md:text-5xl text-center">
          Prendre rendez-vous
        </h2>

        <div className="mt-12 grid sm:grid-cols-2 gap-8">
          <ul className="space-y-5">
            <li className="flex items-start gap-3" data-testid="contact-address">
              <MapPin size={18} className="text-[#b07a63] mt-0.5 shrink-0" />
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  "Ninaa Beauty " + CONTACT.address
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-address-link"
                className="text-[#4b2018] hover:text-[#b07a63] underline-offset-4 hover:underline transition-colors"
              >
                {CONTACT.address}
              </a>
            </li>
            <li className="flex items-start gap-3" data-testid="contact-phone">
              <Phone size={18} className="text-[#b07a63] mt-0.5 shrink-0" />
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="text-[#4b2018] hover:text-[#b07a63] transition-colors">
                {CONTACT.phone}
              </a>
            </li>
            <li className="flex items-start gap-3" data-testid="contact-email">
              <Mail size={18} className="text-[#b07a63] mt-0.5 shrink-0" />
              <a href={`mailto:${CONTACT.email}`} className="text-[#4b2018] hover:text-[#b07a63] transition-colors">
                {CONTACT.email}
              </a>
            </li>
          </ul>

          <div data-testid="contact-hours">
            <div className="flex items-center gap-2 text-[#b07a63] mb-3">
              <Clock size={18} />
              <span className="text-xs tracking-[0.2em] uppercase">Horaires</span>
            </div>
            <ul className="space-y-2">
              {CONTACT.hours.map((h, i) => {
                const active = i === today.index;
                return (
                  <li
                    key={h.d}
                    data-testid={active ? "hours-today" : `hours-row-${i}`}
                    className={`flex justify-between items-center gap-3 text-sm text-[#4b2018] border-b border-[#ece4d7] ${
                      active
                        ? "py-1 md:py-0 md:pb-2 bg-[#f6f1e9] -mx-2 px-2 rounded-sm"
                        : "pb-2"
                    }`}
                  >
                    <span className={active ? "font-semibold" : ""}>{h.d}</span>
                    <span className="flex items-center gap-2 text-right">
                      <span>{h.h}</span>
                      {active && (
                        <span
                          data-testid="hours-status-pill"
                          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] leading-none tracking-wide whitespace-nowrap self-center ${
                            today.open
                              ? "bg-[#e6ede1] text-[#5c7052]"
                              : "bg-[#f0d7d4] text-[#a1564d]"
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                              today.open ? "bg-[#7d9a6e]" : "bg-[#c65f54]"
                            }`}
                          />
                          {today.open ? "Ouvert actuellement" : "Fermé actuellement"}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            <a
              href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
              data-testid="contact-call-btn"
              className="mt-6 flex items-center justify-center gap-2 rounded-full bg-[#4e231b] text-white py-3.5 text-sm tracking-wide hover:bg-[#b07a63] transition-colors duration-400"
            >
              <Phone size={16} /> Appeler l&apos;institut
            </a>
          </div>
        </div>

        {/* Préparer votre rendez-vous — informations pratiques (repliées) */}
        <Reveal id="preparer-rdv" className="mt-8 scroll-mt-24">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem
              value="prepare"
              data-testid="prepare-accordion"
              className="border border-[#ece4d7] rounded-md px-5 bg-[#faf7f2]"
            >
              <AccordionTrigger className="text-sm tracking-wide text-[#4b2018] hover:no-underline py-4">
                <span className="underline decoration-[#b07a63] decoration-1 underline-offset-[6px]">
                  Préparez votre rendez-vous
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-5 pb-2">
                  {PREPARE_VISIT.map((item) => (
                    <div key={item.t} data-testid={`prepare-${item.t}`}>
                      <p className="text-xs tracking-[0.2em] uppercase text-[#b07a63] mb-1.5">
                        {item.t}
                      </p>
                      <p className="text-sm leading-relaxed text-[#4b2018] whitespace-pre-line">
                        {item.d}
                      </p>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Reveal>

        {isHome && (
          <div data-testid="contact-map-wrap" className="mt-10">
            <div
              data-testid="contact-map"
              className="overflow-hidden rounded-md border border-[#ece4d7]"
            >
              <iframe
                title="Localisation de l'institut Ninaa Beauty"
                src={`https://www.google.com/maps?q=${encodeURIComponent(
                  CONTACT.googlePlaceName + ", " + CONTACT.address
                )}&output=embed`}
                className="w-full h-64 md:h-72"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                CONTACT.googlePlaceName + ", " + CONTACT.address
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-directions-btn"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#2b2a27] text-[#4b2018] px-6 py-3 text-sm tracking-wide hover:bg-[#2b2a27] hover:text-white transition-colors duration-400"
            >
              <Navigation size={16} /> Itinéraire
            </a>
          </div>
        )}

        <div className="mt-16 pt-6 border-t border-[#ece4d7] flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-[#4b2018]">
          <div className="flex items-center gap-3">
            <span className="font-serif-display text-base text-[#4b2018]">Ninaa Beauty</span>
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="Instagram Ninaa Beauty"
              className="text-[#b07a63] hover:text-[#4b2018] transition-colors"
            >
              <Instagram size={17} />
            </a>
          </div>
          <span>© {new Date().getFullYear()} — Institut de beauté & bien-être</span>
        </div>

        <nav
          data-testid="footer-legal"
          className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-[#a79a86]"
        >
          <Link
            to="/mentions-legales"
            className="hover:text-[#4b2018] transition-colors"
          >
            Mentions légales
          </Link>
          <span aria-hidden="true" className="text-[#d8c9b3]">•</span>
          <Link
            to="/politique-confidentialite"
            className="hover:text-[#4b2018] transition-colors"
          >
            Politique de confidentialité
          </Link>
          <span aria-hidden="true" className="text-[#d8c9b3]">•</span>
          <button
            type="button"
            data-testid="footer-cookie-settings"
            onClick={openCookieSettings}
            className="hover:text-[#4b2018] transition-colors"
          >
            Gestion des cookies
          </button>
        </nav>
      </Reveal>
    </footer>
  );
};
